/**
 * Phase 15.4.0 — Unified Share-Card Export Utility
 * ------------------------------------------------
 * Hardens the existing share/recap card pipeline against the five
 * recurring production bugs:
 *   1. Blank profile photos
 *   2. Missing cover art
 *   3. Preview / download mismatch
 *   4. Fallback image gaps
 *   5. html-to-image CORS / tainted-canvas blanks
 *   6. iOS Safari blob download silently failing
 *
 * Pipeline
 *   bakeImage(url)              → data: URI (never returns a remote URL).
 *                                 Strategy: direct CORS fetch ▸ Cloudinary
 *                                 transform ▸ backend `/share/proxy-image`
 *                                 ▸ branded SVG placeholder.
 *
 *   exportNodeToBlob(node)      → waits for every <img> inside the node to
 *                                 `decode()` so html-to-image never races a
 *                                 half-loaded bitmap, then rasterises with
 *                                 deterministic options.
 *
 *   downloadBlob(blob, name)    → iOS Safari falls back to a new-tab
 *                                 preview (long-press → Save to Photos)
 *                                 because <a download> with blob: URLs
 *                                 silently fails on iOS Safari.
 *
 *   shareOrDownload(...)        → tries the Web Share API first, falls
 *                                 back to downloadBlob on failure.
 *
 * Designed to be DROP-IN. Callers replace their bespoke `imageToDataUri`
 * + download helpers with calls into this module.
 */
import { getDefaultAvatar, getDefaultCover } from "@/lib/mediaUrls";

// ---- Cloudinary delivery transform ---------------------------------------

const cloudinaryTransform = (url, width = 1080) => {
  if (!url || !url.includes("res.cloudinary.com") || !url.includes("/upload/")) return url;
  // Already transformed? Leave it alone.
  if (/\/upload\/[^/]*(f_|q_)/.test(url)) return url;
  return url.replace("/upload/", `/upload/f_auto,q_auto,w_${width}/`, 1);
};

// ---- Backend proxy helper ------------------------------------------------

const PROXY_HOST_PATTERNS = [
  /(^|\.)res\.cloudinary\.com$/i,
  /(^|\.)googleusercontent\.com$/i,
  /(^|\.)ggpht\.com$/i,
  /(^|\.)gstatic\.com$/i,
  /(^|\.)fbcdn\.net$/i,
  /(^|\.)twimg\.com$/i,
  /(^|\.)unsplash\.com$/i,
  /(^|\.)emergentagent\.com$/i,
  /(^|\.)rgmstudio\.org$/i,
];

const canProxy = (url) => {
  try {
    const u = new URL(url);
    if (u.protocol !== "https:") return false;
    return PROXY_HOST_PATTERNS.some((r) => r.test(u.hostname));
  } catch {
    return false;
  }
};

const proxyUrlFor = (url) => {
  const backend = process.env.REACT_APP_BACKEND_URL || "";
  return `${backend}/api/share/proxy-image?url=${encodeURIComponent(url)}`;
};

// ---- Blob → data URI via FileReader -------------------------------------

const blobToDataUri = (blob) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onloadend = () => resolve(r.result);
  r.onerror = () => reject(r.error || new Error("FileReader failed"));
  r.readAsDataURL(blob);
});

// ---- Single fetch attempt with strict success criteria ------------------

const tryFetchAsDataUri = async (url, init = {}) => {
  const res = await fetch(url, { mode: "cors", credentials: "omit", ...init });
  if (!res.ok) throw new Error(`status ${res.status}`);
  const blob = await res.blob();
  if (!blob.type.startsWith("image/")) throw new Error(`type ${blob.type}`);
  const uri = await blobToDataUri(blob);
  if (typeof uri !== "string" || !uri.startsWith("data:image/")) {
    throw new Error("non-image data uri");
  }
  // Force-decode so html-to-image sees a fully-decoded bitmap.
  try {
    const img = new Image();
    img.src = uri;
    if (img.decode) await img.decode();
  } catch { /* best-effort */ }
  return uri;
};

/**
 * Bake any image URL into a CORS-safe data: URI.
 *
 * Multi-strategy:
 *   1. URL is empty/null  → fallback placeholder
 *   2. URL is data:       → return as-is
 *   3. Cloudinary transform + direct fetch
 *   4. Backend image proxy fetch
 *   5. Final fallback     → branded placeholder
 *
 * @param {string|null|undefined} url
 * @param {{kind?: "cover"|"avatar"}} opts
 * @returns {Promise<{uri: string, source: "input"|"data"|"direct"|"proxy"|"fallback"}>}
 */
export const bakeImage = async (url, opts = {}) => {
  const fallback =
    opts.kind === "avatar" ? getDefaultAvatar() : getDefaultCover();

  if (!url) return { uri: fallback, source: "fallback" };
  if (typeof url === "string" && url.startsWith("data:")) {
    return { uri: url, source: "data" };
  }

  // Strategy 1: direct fetch (Cloudinary-transformed where applicable).
  const directUrl = cloudinaryTransform(url, opts.kind === "avatar" ? 400 : 1080);
  try {
    const uri = await tryFetchAsDataUri(directUrl);
    return { uri, source: "direct" };
  } catch { /* fall through */ }

  // Strategy 2: backend proxy (CORS-safe by definition).
  if (canProxy(url)) {
    try {
      const uri = await tryFetchAsDataUri(proxyUrlFor(directUrl));
      return { uri, source: "proxy" };
    } catch { /* fall through */ }
  }

  // Strategy 3: branded placeholder — guarantees a non-blank export.
  return { uri: fallback, source: "fallback" };
};

/**
 * Bake cover + avatar (+ optional supporter avatar) in parallel.
 * Returns guaranteed non-empty data: URIs.
 */
export const bakeAll = async ({ cover, avatar, supporter } = {}) => {
  const [c, a, s] = await Promise.all([
    bakeImage(cover, { kind: "cover" }),
    bakeImage(avatar, { kind: "avatar" }),
    supporter !== undefined ? bakeImage(supporter, { kind: "avatar" }) : Promise.resolve(null),
  ]);
  return {
    cover: c.uri,
    avatar: a.uri,
    supporter: s ? s.uri : null,
    coverSource: c.source,
    avatarSource: a.source,
    supporterSource: s ? s.source : null,
    // Convenience for "warn artist their image didn't embed" banners.
    coverFellBack: c.source === "fallback" && !!cover,
    avatarFellBack: a.source === "fallback" && !!avatar,
  };
};

// ---- Pre-export image decode wait ---------------------------------------

const waitForImages = async (node) => {
  if (!node) return;
  const imgs = Array.from(node.querySelectorAll("img"));
  await Promise.all(imgs.map(async (img) => {
    try {
      if (img.decode) await img.decode();
      else if (!img.complete) {
        await new Promise((resolve) => {
          img.addEventListener("load", resolve, { once: true });
          img.addEventListener("error", resolve, { once: true });
        });
      }
    } catch { /* swallow */ }
  }));
};

// ---- Export node → blob (deterministic) ---------------------------------

/**
 * Rasterise a DOM node into a PNG blob. Always waits for images +
 * two animation frames so the preview matches the export.
 */
export const exportNodeToBlob = async (node, opts = {}) => {
  if (!node) return null;
  const { toBlob } = await import("html-to-image");

  // Two frames so React's commit + browser layout are flushed before
  // html-to-image walks the DOM.
  await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
  await waitForImages(node);
  // One more frame after decode so any layout shift from late-decoded
  // images has time to settle.
  await new Promise((r) => requestAnimationFrame(r));

  return await toBlob(node, {
    pixelRatio: opts.pixelRatio ?? 1,
    cacheBust: false,
    backgroundColor: opts.backgroundColor ?? "#07020d",
    skipFonts: true,
  });
};

// ---- iOS Safari-safe download ------------------------------------------

const isIOS = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  // iPad on iOS 13+ identifies as Mac with touch. The MaxTouchPoints
  // check covers that case without false positives on actual desktops.
  return /iPhone|iPad|iPod/i.test(ua) ||
    (ua.includes("Mac") && navigator.maxTouchPoints > 1);
};

/**
 * Download a blob. On iOS Safari (which silently drops <a download>
 * for blob: URLs), open the blob in a new tab so the user can
 * long-press → "Save to Photos".
 */
export const downloadBlob = (blob, filename) => {
  if (!blob) return false;
  const url = URL.createObjectURL(blob);

  if (isIOS()) {
    // iOS Safari ignores <a download> for blob:. Open the image so
    // the user can long-press → Save to Photos. Same outcome as
    // Web Share would deliver, but works on every iOS browser.
    const w = window.open(url, "_blank");
    if (!w) {
      // Pop-up blocked: fall back to normal anchor click (will at
      // least navigate the user to the image preview).
      const a = document.createElement("a");
      a.href = url;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
    // Don't immediately revoke — the new tab needs the URL alive.
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
    return true;
  }

  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 2000);
  return true;
};

/**
 * One-call helper: try Web Share API with files first, fall back to
 * downloadBlob. Returns "shared" | "downloaded" | "failed".
 */
export const shareOrDownload = async ({ blob, filename, title, text, url }) => {
  if (!blob) return "failed";
  try {
    const file = new File([blob], filename, { type: "image/png" });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({ files: [file], title, text, url });
      return "shared";
    }
  } catch (e) {
    if (e?.name === "AbortError") return "failed";
    // Fall through to download.
  }
  return downloadBlob(blob, filename) ? "downloaded" : "failed";
};

export const _internals = { cloudinaryTransform, canProxy, isIOS };
